import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Building2, CalendarRange, LineChart } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import type { ComercialUnitRow } from "@/services/comercial";
import {
  fetchChaoUcStageSchedules,
  type ChaoUcStageScheduleDto,
  type ChaoUnitMetrics,
} from "@/services/chaoDeObra";
import { formatIsoDateBr, formatNumber } from "@/utils/format";
import { cn } from "@/utils/cn";
import {
  computeAvancoFisicoPorUc,
  countUnitsSemUc,
} from "./chaoUnitCardUtils";
import { useAppAccess } from "@/hooks/useAppAccess";
import { formatStageDurationMessage } from "./chaoUcStageDuration";
import { ChaoUcEtapaDatasModal } from "./ChaoUcEtapaDatasModal";

interface Props {
  open: boolean;
  onClose: () => void;
  units: ComercialUnitRow[];
  metrics: Record<string, ChaoUnitMetrics>;
  appliedCostCenter: string[];
  unitLinksByCc: Record<string, Record<string, string>>;
  obraGeneralPct: number;
}

function barColor(pct: number): string {
  if (pct >= 99) return "bg-emerald-500";
  if (pct >= 50) return "bg-blue-500";
  if (pct > 0) return "bg-amber-400";
  return "bg-slate-300";
}

function hasStageInfo(s?: ChaoUcStageScheduleDto | null): boolean {
  if (!s) return false;
  return Boolean(s.startDate || s.endDate || s.startPhoto || s.endPhoto);
}

function formatStageRange(s?: ChaoUcStageScheduleDto | null): string | null {
  if (!s?.startDate && !s?.endDate) return null;
  const start = s.startDate ? formatIsoDateBr(s.startDate) : "—";
  const end = s.endDate ? formatIsoDateBr(s.endDate) : "—";
  return `${start} — ${end}`;
}

export function ChaoAvancoFisicoPorUcModal({
  open,
  onClose,
  units,
  metrics,
  appliedCostCenter,
  unitLinksByCc,
  obraGeneralPct,
}: Props) {
  const { isAdmin, isMaster } = useAppAccess();
  const canEditSchedule = isAdmin || isMaster;
  const costCenterId = appliedCostCenter[0] ?? "";
  const [schedules, setSchedules] = useState<Record<string, ChaoUcStageScheduleDto>>({});
  const [etapaLabel, setEtapaLabel] = useState<string | null>(null);

  const rows = useMemo(
    () => computeAvancoFisicoPorUc(units, metrics, appliedCostCenter, unitLinksByCc),
    [units, metrics, appliedCostCenter, unitLinksByCc],
  );

  const semUc = useMemo(
    () => countUnitsSemUc(units, appliedCostCenter, unitLinksByCc),
    [units, appliedCostCenter, unitLinksByCc],
  );

  const loadSchedules = useCallback(async () => {
    if (!costCenterId) {
      setSchedules({});
      return;
    }
    try {
      const res = await fetchChaoUcStageSchedules(costCenterId);
      setSchedules(res.success ? res.schedules ?? {} : {});
    } catch {
      setSchedules({});
    }
  }, [costCenterId]);

  useEffect(() => {
    if (!open) {
      setEtapaLabel(null);
      return;
    }
    void loadSchedules();
  }, [open, loadSchedules]);

  const totalComUc = rows.reduce((s, r) => s + r.unitCount, 0);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title="Avanço físico por unidade construtiva"
        size="xl"
        closeOnBackdropClick={!etapaLabel}
        closeOnEscape={!etapaLabel}
        footer={
          <Button type="button" variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        }
      >
        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-card sm:px-6">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  Média geral da obra
                </p>
                <p className="mt-1.5 text-2xl font-bold text-slate-900 tabular-nums leading-none">
                  {formatNumber(obraGeneralPct, 1)}%
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  {totalComUc} unidade{totalComUc !== 1 ? "s" : ""} com UC vinculada
                  {semUc > 0 ? ` · ${semUc} sem UC` : ""}
                </p>
              </div>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <LineChart className="h-5 w-5" aria-hidden />
              </div>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-blue-500 transition-all duration-500"
                style={{ width: `${Math.min(100, Math.max(0, obraGeneralPct))}%` }}
              />
            </div>
          </div>

          {rows.length === 0 ? (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">Nenhuma unidade construtiva vinculada</p>
                <p className="mt-1 text-xs text-amber-800">
                  Use <strong>Vínculos UC</strong> para associar as unidades de imóvel às UCs do orçamento e visualizar
                  as médias por grupo.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Média por unidade construtiva
              </p>
              <ul className="space-y-2.5">
                {rows.map((row) => {
                  const schedule = schedules[row.label];
                  const filled = hasStageInfo(schedule);
                  const range = formatStageRange(schedule);
                  const durationMsg = formatStageDurationMessage(schedule);
                  return (
                    <li
                      key={row.label}
                      className="rounded-2xl border border-slate-200 bg-slate-50/60 px-5 py-4 transition-colors hover:bg-white sm:px-6"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-800 leading-snug">{row.label}</p>
                          <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500">
                            <Building2 className="h-3.5 w-3.5 shrink-0" />
                            {row.unitCount} unidade{row.unitCount !== 1 ? "s" : ""}
                            {range ? <span className="text-slate-400">· {range}</span> : null}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <button
                            type="button"
                            disabled={!costCenterId}
                            onClick={() => setEtapaLabel(row.label)}
                            title={
                              !costCenterId
                                ? "Selecione um centro de custo para ver as datas"
                                : canEditSchedule
                                  ? "Datas da etapa"
                                  : "Ver datas da etapa"
                            }
                            aria-label={`Datas da etapa ${row.label}`}
                            className={cn(
                              "inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors",
                              filled
                                ? "border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
                                : "border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-700",
                              !costCenterId && "cursor-not-allowed opacity-50",
                            )}
                          >
                            <CalendarRange className="h-4 w-4" />
                          </button>
                          <p className="text-lg font-bold tabular-nums text-slate-900">
                            {formatNumber(row.avgPct, 1)}%
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200/80">
                        <div
                          className={cn("h-full rounded-full transition-all duration-500", barColor(row.avgPct))}
                          style={{ width: `${Math.min(100, Math.max(0, row.avgPct))}%` }}
                        />
                      </div>
                      {durationMsg ? (
                        <p className="mt-2.5 text-xs text-slate-500">{durationMsg}</p>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {semUc > 0 && rows.length > 0 && (
            <p className="text-xs text-slate-500">
              {semUc} unidade{semUc !== 1 ? "s" : ""} sem vínculo de UC não entra{semUc === 1 ? "" : "m"} neste
              agrupamento.
            </p>
          )}
        </div>
      </Modal>

      <ChaoUcEtapaDatasModal
        open={!!etapaLabel}
        onClose={() => setEtapaLabel(null)}
        costCenterId={costCenterId}
        ucLabel={etapaLabel ?? ""}
        canEdit={canEditSchedule}
        onSaved={(schedule) => {
          if (!etapaLabel) return;
          setSchedules((prev) => ({ ...prev, [etapaLabel]: schedule }));
        }}
      />
    </>
  );
}
